import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { ROOMS } from '../../rooms';
import { useWorldStore, ZOOM } from '../../state/worldStore';
import { useCityStore } from '../../city/cityStore';
import { useCutscene, cutsceneBridge } from '../../city/cutscene';
import { stateAt } from '../../city/cityMotion';
import { serverNow } from '../../services/worldSocket';

// StoryRoomSwitch — watching an episode, its scene goes INSIDE: the view switches
// into the room, and back out to where you were when the scene is over.
//
// Khari, 2026-10-29: *"A camera fly-in to an indoor room may be too much for
// now. It is better to just switch scenes."* So this is a cut, not a flight:
// the room opens the way walking in does (its own spawn, which frames the story
// marks: rooms.js `storyMarks` stand in front of it, facing it), RoomStory and
// RoomStoryAudio play the scene in there, and when the last inside scene is over
// you come back to the pavement you were watching from, facing the door, and
// the episode carries on (CityCutscene picks the target back up).
//
// Only for someone who stepped into the episode (the cutscene's target). Leave
// while inside and the room is simply yours: nothing pulls you back out. Walk
// out yourself and it lets go. A room that refuses you (a rung, the story gate)
// is not forced: you stay outside with the door.
//
// Mounted in the Canvas, NOT gated on the street, so it survives the switch.
const ENTER_AFTER_MS = 400;      // the lead reaches the door, then the cut
const EXIT_GRACE_MS = 1500;      // let the last word land before cutting back out
const LOOK_DIST = 5;

/** Which story this visitor is watching right now, or null. */
function watchedStory(target, cs, t) {
  if (!target) return null;
  if (target.kind === 'story') return cs.stories?.[target.id] || null;
  if (target.kind === 'artist') {
    const id = Number(target.id);
    return Object.values(cs.stories || {}).find((x) => t >= x.t0 - 60000 && t <= x.t1
      && (x.a === id || (x.scenes || []).some((sc) => sc.w === id && t >= sc.t0 - 60000 && t < sc.t1))) || null;
  }
  return null;
}

/** The inside scene on now, in a room this World has. (The server only sets `r`
    for a room with story marks: rooms.js, or placed in the World.) */
function insideNow(st, t) {
  return (st?.scenes || []).find((sc) => sc.r && ROOMS[sc.r] && t >= sc.t0 + ENTER_AFTER_MS && t < sc.t1) || null;
}

export default function StoryRoomSwitch() {
  const sw = useRef(null);         // { story, room, back: { pos, look } }
  const tried = useRef(new Set()); // `${story}:${scene}` already attempted (a refusal is not retried)
  const acc = useRef(0);

  useFrame(({ camera }, dt) => {
    acc.current += dt;
    if (acc.current < 0.25) return;
    acc.current = 0;
    const ws = useWorldStore.getState();
    const ct = useCutscene.getState();
    const cs = useCityStore.getState();
    const t = serverNow();

    // ── Inside, for a scene ──────────────────────────────────────────────────
    if (sw.current) {
      // They walked out themselves, or went somewhere else: let go.
      if (ws.zoom !== ZOOM.INTERIOR || ws.interior !== sw.current.room) { sw.current = null; return; }
      // They left the episode: the room is theirs now, nothing pulls them out.
      if (!ct.target) { sw.current = null; return; }
      const st = cs.stories?.[sw.current.story];
      const still = (st?.scenes || []).some((sc) => sc.r === sw.current.room && t >= sc.t0 - EXIT_GRACE_MS && t < sc.t1 + EXIT_GRACE_MS);
      if (!still) {
        const back = sw.current.back;
        sw.current = null;
        ws.exitInterior(back);
      }
      return;
    }

    // ── On the street, watching an episode ───────────────────────────────────
    if (ws.zoom !== ZOOM.STREET || !ct.target) return;
    const st = watchedStory(ct.target, cs, t);
    const sc = insideNow(st, t);
    if (!sc) return;
    const key = `${st.id}:${sc.i}`;
    if (tried.current.has(key)) return;
    tried.current.add(key);

    // Where they come back to: where they really stand (the cutscene's saved
    // view while it holds the camera), facing the door the lead went through.
    const at = (cutsceneBridge.active && cutsceneBridge.at) || camera.position;
    const lead = cs.byId?.[st.a] && stateAt(cs.byId[st.a], t, cs.convs);
    let look;
    if (lead) look = [lead.x, at.y - 0.05, lead.z];
    else {
      const f = camera.getWorldDirection(camera.position.clone());
      look = [at.x + f.x * LOOK_DIST, at.y + f.y * LOOK_DIST, at.z + f.z * LOOK_DIST];
    }
    const back = { pos: [at.x, at.y, at.z], look };

    // The street lets go of the camera (it unmounts in the room; CityCutscene and
    // CityTalk clean up after themselves too). The episode stays the target.
    cutsceneBridge.active = false; cutsceneBridge.at = null; cutsceneBridge.scene = null;
    ct.setCaption(null);
    if (ct.phase === 'cut') ct.setPhase('follow');

    // `scene`: the artists are setting a scene in there, so a venue's hours do
    // not shut them out (worldStore.enterInterior) — the other doors still do.
    if (!ws.enterInterior(sc.r, { scene: true })) return;   // refused: they stay out here with the door
    sw.current = { story: st.id, room: sc.r, back };
  });

  return null;
}
